"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  ResultTable,
} from "@/components/tools/stats-descriptive/ui";
import {
  DataVerdict,
  RuleCards,
  RuleCurve,
  RuleWorking,
  ruleTable,
} from "@/components/tools/stats-descriptive-more/rule-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";
import {
  ruleRows,
  summarise,
  withCustomK,
} from "@/lib/tool-kit/stats-descriptive-more/empirical-rule";

/** English strings. A translated content file overrides these through `ui`. */
export const EMPIRICAL_RULE_CALCULATOR_STRINGS = {
  modeLabel: "Start from",
  modeValues: "A mean and standard deviation",
  modeData: "A list of data",
  meanLabel: "Mean",
  meanPlaceholder: "72",
  sdLabel: "Standard deviation",
  sdPlaceholder: "8",
  nLabel: "Number of values (optional)",
  nHint: "Gives the expected count inside each interval.",
  kLabel: "Your own number of SDs (optional)",
  kHint:
    "Adds a row for mean ± k SD, with the exact normal share and Chebyshev's bound.",
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "52, 58, 61, 63, 65, 66, 67, 68, 69, 70",
  valuesDetected: "one: value detected|other: values detected",
  tryExample: "Try an example",
  tryData: "Try a data example",
  exampleN: "1000",
  exampleData:
    "52, 58, 61, 63, 65, 66, 67, 68, 69, 70, 70, 71, 72, 72, 73, 74, 75, 76, 77, 78, 80, 81, 83, 86, 95",
  clear: "Clear",
  enterPrompt:
    "Enter a mean and a standard deviation above zero — or switch to a list of data — to see the 68–95–99.7 intervals.",
  enterDataPrompt: "Enter at least two different numbers to see the intervals.",
  results: "Results",
  cardOne: "About 68% of values lie between",
  cardTwo: "About 95% of values lie between",
  cardThree: "About 99.7% of values lie between",
  rangeTemplate: "{from} to {to}",
  percentTemplate: "{value}%",
  kTemplate: "± {k} SD",
  notAvailable: "—",
  tableTitle: "The rule, the exact normal shares and Chebyshev's bound",
  colK: "Distance from the mean",
  colRange: "Interval",
  colRule: "Empirical rule",
  colNormal: "Exact normal share",
  colTail: "Each tail (normal)",
  colChebyshev: "Chebyshev's minimum, any distribution",
  colExpected: "Expected count (normal)",
  colActual: "Actually inside your data",
  actualTemplate: "{count} ({share})",
  tableNote:
    "The empirical rule is a rounded, normal-only summary. Chebyshev's inequality needs no assumption about the shape, but it only guarantees a minimum.",
  curveTitle: "The bell curve, shaded by standard deviations",
  curveLabel:
    "A normal curve with mean {mean} and standard deviation {sd}: the middle band from {one} holds about 68 percent of values, the band from {two} about 95 percent and the band from {three} about 99.7 percent.",
  axisSd: "SD",
  axisValue: "x",
  curveNote:
    "Darkest band: within 1 SD. Medium: within 2 SD. Lightest: within 3 SD.",
  workingTitle: "How the intervals were found",
  workData:
    "From your {n} values: mean = {mean}, sample standard deviation = {sd}",
  workInterval: "Mean ± {k} × SD = {mean} ± {k} × {sd} = {lower} to {upper}",
  workChebyshev:
    "Chebyshev, k = {k}: at least 1 − 1 ÷ {k}² = {value} of any distribution",
  workChebyshevOne:
    "Chebyshev, k = {k}: the bound is only informative above 1 SD, so it guarantees {value}",
  workNote:
    "Chebyshev's inequality: at least 1 − 1 ÷ k² of the values lie within k standard deviations of the mean, for any distribution with a finite variance.",
  copySummary: "Copy intervals",
  copied: "Copied!",
  summaryText:
    "Mean {mean}, SD {sd}: 68% between {l1} and {u1}; 95% between {l2} and {u2}; 99.7% between {l3} and {u3}",
  verdictClose:
    "Your data put {one} within 1 SD and {two} within 2 SD, against 68% and 95% expected for a normal distribution — a close match.",
  verdictFar:
    "Your data put {one} within 1 SD and {two} within 2 SD, against 68% and 95% expected for a normal distribution. With a small sample some gap is normal; a large gap suggests skew, outliers or heavy tails.",
} as const;

type Mode = "values" | "data";

export default function EmpiricalRuleCalculatorWidget() {
  const strings = useToolUi(EMPIRICAL_RULE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("values");
  const [meanText, setMeanText] = useState("");
  const [sdText, setSdText] = useState("");
  const [nText, setNText] = useState("");
  const [kText, setKText] = useState("");
  const [dataText, setDataText] = useState("");

  const data = mode === "data" ? parseNumberList(dataText) : [];
  const summary = mode === "data" ? summarise(data) : null;
  const typedMean = parseNumberInput(meanText);
  const typedSd = parseNumberInput(sdText);
  const mean = mode === "data" ? (summary?.mean ?? null) : typedMean;
  const sd = mode === "data" ? (summary?.sd ?? null) : typedSd;
  const k = parseNumberInput(kText);
  const ks = withCustomK(k);
  const ready = mean !== null && sd !== null && sd > 0;
  const rows = ready
    ? ruleRows(mean, sd, ks, { n: parseIntegerInput(nText), data })
    : [];
  const num = (value: number) => formatStat(fmt, value);
  const view = { strings, fmt, num };
  const table = ruleTable(rows, view);
  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.modeLabel}
          name="er-mode"
          onChange={setMode}
          options={[
            { value: "values", label: strings.modeValues },
            { value: "data", label: strings.modeData },
          ]}
          value={mode}
        />
        {mode === "values" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="er-mean"
              label={strings.meanLabel}
              numeric
              onChange={setMeanText}
              placeholder={strings.meanPlaceholder}
              value={meanText}
            />
            <TextField
              id="er-sd"
              label={strings.sdLabel}
              numeric
              onChange={setSdText}
              placeholder={strings.sdPlaceholder}
              value={sdText}
            />
            <TextField
              hint={strings.nHint}
              id="er-n"
              label={strings.nLabel}
              numeric
              onChange={setNText}
              value={nText}
            />
            <TextField
              hint={strings.kHint}
              id="er-k"
              label={strings.kLabel}
              numeric
              onChange={setKText}
              value={kText}
            />
          </div>
        ) : (
          <TextAreaField
            hint={
              <>
                {fmt(data.length)}
                {tPlural(strings.valuesDetected, locale, data.length)}
              </>
            }
            id="er-data"
            label={strings.dataLabel}
            onChange={setDataText}
            placeholder={strings.dataPlaceholder}
            value={dataText}
          />
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setMode("values");
              setMeanText("72");
              setSdText("8");
              setNText(strings.exampleN);
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setMode("data");
              setDataText(strings.exampleData);
            }}
            type="button"
          >
            {strings.tryData}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setMeanText("");
              setSdText("");
              setNText("");
              setKText("");
              setDataText("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {ready ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <RuleCards rows={rows} view={view} />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copySummary}
              text={t(strings.summaryText, {
                mean: num(mean),
                sd: num(sd),
                l1: num(rows[0].lower),
                u1: num(rows[0].upper),
                l2: num(rows[1].lower),
                u2: num(rows[1].upper),
                l3: num(rows[2].lower),
                u3: num(rows[2].upper),
              })}
            />
          </div>
          <RuleCurve mean={mean} sd={sd} view={view} />
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
            <ResultTable head={table.head} rows={table.body} />
            <Explanation>{strings.tableNote}</Explanation>
            <DataVerdict rows={rows} view={view} />
          </div>
          <RuleWorking
            fromData={summary ? summary.n : null}
            mean={mean}
            rows={rows}
            sd={sd}
            view={view}
          />
        </section>
      ) : (
        <Notice>
          {mode === "data" ? strings.enterDataPrompt : strings.enterPrompt}
        </Notice>
      )}
    </>
  );
}
